import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AuthLayout } from './AuthLayout';
import { SigninForm, renderSigninFooter } from './SigninScreen';
import { VerifiedTakeover } from './verify/VerifiedTakeover';
import { AuthError, AuthField, AuthHeading, AuthPrimaryButton, authLinkClass } from './AuthControls';
import { useForm } from 'hooks/useForm';
import { useAuth } from 'hooks/useAuth';
import { trackUmami } from '@/lib/umami/umami';

const ACTIVATED_COPY = {
  label: 'Account active',
  title: "You're all set.",
  body: 'Your password is saved and your account is active. Sign in to open your workspace.',
} as const;

type StateType = {
  password: string;
  confirmPassword: string;
};

export const AcceptInvitation = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(false);
  const { resetPassword } = useAuth();
  const { state, formRef, update } = useForm<StateType>();
  const { password, confirmPassword } = state;

  useEffect(() => trackUmami('invitation-opened'), []);

  const onSubmit = async () => {
    if (isDisabled) return;
    setError(null);
    setIsSubmitting(true);
    // Read from the URL at submit time rather than holding it in form state: it is
    // not something the user types, and a hidden field only invites it to be lost.
    const verificationCode = new URL(window.location.toString()).searchParams.get('verificationCode') || '';
    try {
      await resetPassword({ password, verificationCode });
      trackUmami('invitation-accepted');
      // The panel expands over the page (see the takeover below); "Sign in" slides it
      // away onto the sign-in form, so there is no toast and no redirect to wait for.
      setIsActivated(true);
    } catch (ex) {
      trackUmami('invitation-failed');
      update({ password: '', confirmPassword: '' });
      const { message } = ex as { message?: string };
      setError(message || 'Could not activate your account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = isSubmitting || !password || password !== confirmPassword;

  return (
    <AuthLayout
      takeover={
        isActivated
          ? ({ close }) => <VerifiedTakeover status="verified" copy={ACTIVATED_COPY} onContinue={close} />
          : undefined
      }
      takeoverLabel="Account activated"
      // Replaced, not pushed: Back from the form must not return to a link that is used up.
      onClosed={() => navigate('/auth/signin', { replace: true })}
      // Once active, the page behind the panel is the sign-in form, so that is what "Sign in" reveals.
      footer={
        isActivated ? (
          renderSigninFooter
        ) : (
          <span>
            Already have an account?{' '}
            <Link to="/auth/signin" className={`${authLinkClass} font-medium`}>
              Sign in
            </Link>
          </span>
        )
      }
    >
      {isActivated ? (
        <SigninForm />
      ) : (
        <>
          <AuthHeading title="Accept your invitation" subtitle="Set a password to activate your account." />

          <form onSubmit={onSubmit} ref={formRef} className="flex flex-col gap-[18px]">
            {!!error && <AuthError>{error}</AuthError>}

            <AuthField label="New password" type="password" name="password" autoComplete="new-password" />
            <AuthField label="Confirm password" type="password" name="confirmPassword" autoComplete="new-password" />

            <AuthPrimaryButton disabled={isDisabled} isPending={isSubmitting}>
              {isSubmitting ? 'Activating…' : 'Activate account'}
            </AuthPrimaryButton>
          </form>
        </>
      )}
    </AuthLayout>
  );
};
